import { useState } from 'react';
import { useSEO } from '../../utils/seo';

export default function Home() {
  const [selectedDevice, setSelectedDevice] = useState('nano-s-plus');
  const [currentStep, setCurrentStep] = useState(1);

  // SEO Configuration with Schema.org
  useSEO({
    title: 'Get Started with Ledger® | Ledger.com/start®',
    description: 'Manage crypto effortlessly: send, receive, trade, and monitor prices starting from ledger.com/start securely.',
    keywords: 'Ledger, hardware wallet, cryptocurrency, Bitcoin, Ethereum, crypto security, digital assets',
    schema: {
      '@type': 'WebPage',
      name: 'Get Started with Ledger® | Ledger.com/start®',
      description: 'Manage crypto effortlessly: send, receive, trade, and monitor prices starting from ledger.com/start securely.',
      publisher: {
        '@type': 'Organization',
        name: 'Ledger',
        logo: {
          '@type': 'ImageObject',
          url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/vite.svg`
        }
      },
      mainEntity: {
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'What is a hardware wallet?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'A hardware wallet is a physical device that stores your cryptocurrency private keys offline, providing the highest level of security for your digital assets.'
            }
          },
          {
            '@type': 'Question',
            name: 'How do I set up my Ledger device?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Simply follow our 6-step setup guide: unbox your device, download Ledger Live, set up your device, create a PIN, write down your recovery phrase, and install apps.'
            }
          },
          {
            '@type': 'Question',
            name: 'What happens if I lose my Ledger device?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'If you lose your device, you can restore your wallet using your 24-word recovery phrase on a new Ledger device or compatible wallet software.'
            }
          },
          {
            '@type': 'Question',
            name: 'Can I use Ledger with mobile devices?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Yes, Ledger Nano X supports Bluetooth connectivity for mobile use. You can manage your crypto on the go with the Ledger Live mobile app.'
            }
          }
        ]
      }
    }
  });

  const devices = [
    {
      id: 'nano-s-plus',
      name: 'Ledger Nano S Plus',
      price: '$79',
      image: 'https://public.readdy.ai/ai/img_res/46579b2564dc76889c19c69c7f038177.jpg',
      features: ['1,100+ coins supported', 'USB-C connectivity', 'Secure Element chip']
    },
    {
      id: 'nano-x',
      name: 'Ledger Nano X',
      price: '$149',
      image: 'https://readdy.ai/api/search-image?query=Ledger%20Nano%20X%20hardware%20cryptocurrency%20wallet%20device%20with%20bluetooth%20on%20clean%20white%20background%2C%20modern%20minimalist%20product%20photography%2C%20professional%20lighting%2C%20premium%20tech%20product&width=300&height=200&seq=2&orientation=landscape',
      features: ['5,500+ coins supported', 'Bluetooth connectivity', 'Mobile app support']
    },
    {
      id: 'stax',
      name: 'Ledger Stax',
      price: '$279',
      image: 'https://readdy.ai/api/search-image?query=Ledger%20Stax%20premium%20hardware%20cryptocurrency%20wallet%20with%20curved%20E%20Ink%20screen%20on%20clean%20white%20background%2C%20modern%20minimalist%20product%20photography%2C%20professional%20lighting%2C%20luxury%20tech%20device&width=300&height=200&seq=3&orientation=landscape',
      features: ['Curved E Ink screen', 'Wireless charging', 'Premium design']
    }
  ];

  const setupSteps = [
    {
      step: 1,
      title: 'Unbox your Ledger',
      description: 'Check that your device is genuine and hasn\'t been tampered with',
      icon: 'ri-box-3-line'
    },
    {
      step: 2,
      title: 'Download Ledger Live',
      description: 'Install the official Ledger Live app on your computer or mobile',
      icon: 'ri-download-line'
    },
    {
      step: 3,
      title: 'Set up your device',
      description: 'Follow the on-screen instructions to initialize your Ledger',
      icon: 'ri-settings-3-line'
    },
    {
      step: 4,
      title: 'Create your PIN',
      description: 'Choose a secure PIN code to protect your device',
      icon: 'ri-lock-line'
    },
    {
      step: 5,
      title: 'Write down your recovery phrase',
      description: 'Safely store your 24-word recovery phrase offline',
      icon: 'ri-file-text-line'
    },
    {
      step: 6,
      title: 'Install apps',
      description: 'Add cryptocurrency apps to manage your digital assets',
      icon: 'ri-apps-line'
    }
  ];

  const securityFeatures = [
    {
      title: 'Secure Element',
      description: 'Bank-grade security chip protects your private keys',
      icon: 'ri-shield-check-line'
    },
    {
      title: 'PIN Protection',
      description: 'Your device is locked with a PIN code you choose',
      icon: 'ri-key-line'
    },
    {
      title: 'Recovery Phrase',
      description: '24-word backup to restore your wallet if needed',
      icon: 'ri-file-shield-line'
    },
    {
      title: 'Open Source',
      description: 'Transparent code audited by security experts',
      icon: 'ri-code-line'
    }
  ];

  const supportedCoins = [
    { name: 'Bitcoin', symbol: 'BTC', icon: 'ri-bit-coin-line' },
    { name: 'Ethereum', symbol: 'ETH', icon: 'ri-currency-line' },
    { name: 'Cardano', symbol: 'ADA', icon: 'ri-currency-line' },
    { name: 'Solana', symbol: 'SOL', icon: 'ri-currency-line' },
    { name: 'Polygon', symbol: 'MATIC', icon: 'ri-currency-line' },
    { name: 'Chainlink', symbol: 'LINK', icon: 'ri-currency-line' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-black" style={{fontFamily: '"Pacifico", serif'}}>
                Ledger
              </h1>
            </div>
            <nav className="hidden md:flex space-x-8">
              <a href="#products" className="text-gray-700 hover:text-black cursor-pointer">Products</a>
              <a href="#setup" className="text-gray-700 hover:text-black cursor-pointer">Setup Guide</a>
              <a href="#security" className="text-gray-700 hover:text-black cursor-pointer">Security</a>
              <a href="#support" className="text-gray-700 hover:text-black cursor-pointer">Support</a>
            </nav>
            <div className="flex items-center space-x-4">
              <button className="bg-black text-white px-6 py-2 rounded-full hover:bg-gray-800 whitespace-nowrap cursor-pointer">
                Get Started
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 to-indigo-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl font-bold text-gray-900 mb-6">
                Secure Your Crypto with Ledger
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                The most trusted hardware wallet for storing Bitcoin, Ethereum, and 5,500+ cryptocurrencies. 
                Get started in minutes with our step-by-step setup guide.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button className="bg-black text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-gray-800 whitespace-nowrap cursor-pointer">
                  Start Setup Guide
                </button>
                <button className="border-2 border-black text-black px-8 py-4 rounded-full text-lg font-semibold hover:bg-black hover:text-white whitespace-nowrap cursor-pointer">
                  Shop Devices
                </button>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Multiple%20Ledger%20hardware%20cryptocurrency%20wallets%20arranged%20elegantly%20on%20clean%20white%20background%2C%20modern%20tech%20product%20photography%2C%20professional%20lighting%2C%20premium%20security%20devices%20showcase&width=600&height=400&seq=4&orientation=landscape"
                alt="Ledger Hardware Wallets"
                className="w-full h-auto rounded-2xl shadow-2xl object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Device Selection */}
      <section id="products" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Choose Your Ledger Device</h2>
            <p className="text-xl text-gray-600">Find the perfect hardware wallet for your needs</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {devices.map((device) => (
              <div 
                key={device.id}
                className={`bg-white border-2 rounded-2xl p-8 cursor-pointer transition-all duration-300 hover:shadow-xl ${
                  selectedDevice === device.id ? 'border-black shadow-lg' : 'border-gray-200'
                }`}
                onClick={() => setSelectedDevice(device.id)}
              >
                <img 
                  src={device.image}
                  alt={device.name}
                  className="w-full h-48 object-cover rounded-xl mb-6 object-top"
                />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{device.name}</h3>
                <p className="text-3xl font-bold text-black mb-4">{device.price}</p>
                <ul className="space-y-2">
                  {device.features.map((feature, index) => (
                    <li key={index} className="flex items-center text-gray-600">
                      <i className="ri-check-line text-green-500 mr-2 w-5 h-5 flex items-center justify-center"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
                <button className="w-full mt-6 bg-black text-white py-3 rounded-full font-semibold hover:bg-gray-800 whitespace-nowrap cursor-pointer">
                  Select Device
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Setup Guide */}
      <section id="setup" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Easy Setup in 6 Steps</h2>
            <p className="text-xl text-gray-600">Follow our guided setup process to secure your crypto</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {setupSteps.map((step) => (
              <div 
                key={step.step}
                className={`bg-white rounded-2xl p-8 cursor-pointer transition-all duration-300 hover:shadow-lg ${
                  currentStep === step.step ? 'ring-2 ring-black shadow-lg' : ''
                }`}
                onClick={() => setCurrentStep(step.step)}
              >
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-black text-white rounded-full flex items-center justify-center mr-4">
                    <i className={`${step.icon} text-xl w-6 h-6 flex items-center justify-center`}></i>
                  </div>
                  <span className="text-2xl font-bold text-gray-900">Step {step.step}</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <button className="bg-black text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-gray-800 whitespace-nowrap cursor-pointer">
              Start Setup Process
            </button>
          </div>
        </div>
      </section>

      {/* Security Features */}
      <section id="security" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Bank-Grade Security</h2>
            <p className="text-xl text-gray-600">Your crypto is protected by multiple layers of security</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {securityFeatures.map((feature, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-black text-white rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${feature.icon} text-2xl w-8 h-8 flex items-center justify-center`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Supported Cryptocurrencies */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">5,500+ Cryptocurrencies Supported</h2>
            <p className="text-xl text-gray-600">Store and manage all your favorite digital assets</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 mb-12">
            {supportedCoins.map((coin, index) => (
              <div key={index} className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-shadow cursor-pointer">
                <i className={`${coin.icon} text-4xl text-black mb-3 w-12 h-12 flex items-center justify-center mx-auto`}></i>
                <h3 className="font-bold text-gray-900">{coin.name}</h3>
                <p className="text-gray-600 text-sm">{coin.symbol}</p>
              </div>
            ))}
          </div>

          <div className="text-center">
            <button className="border-2 border-black text-black px-8 py-3 rounded-full font-semibold hover:bg-black hover:text-white whitespace-nowrap cursor-pointer">
              View All Supported Coins
            </button>
          </div>
        </div>
      </section>

      {/* Download Section */}
      <section className="py-20 bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">Download Ledger Live</h2>
              <p className="text-xl text-gray-300 mb-8">
                The official companion app to manage your Ledger device. Available for desktop and mobile.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button className="bg-white text-black px-8 py-4 rounded-full font-semibold hover:bg-gray-100 whitespace-nowrap cursor-pointer">
                  <i className="ri-windows-line mr-2 w-5 h-5 flex items-center justify-center"></i>
                  Download for Windows
                </button>
                <button className="bg-white text-black px-8 py-4 rounded-full font-semibold hover:bg-gray-100 whitespace-nowrap cursor-pointer">
                  <i className="ri-apple-line mr-2 w-5 h-5 flex items-center justify-center"></i>
                  Download for Mac
                </button>
              </div>
            </div>
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=Ledger%20Live%20app%20interface%20on%20computer%20screen%20and%20mobile%20phone%2C%20modern%20cryptocurrency%20portfolio%20management%20software%2C%20clean%20UI%20design%2C%20professional%20tech%20photography&width=600&height=400&seq=5&orientation=landscape"
                alt="Ledger Live App"
                className="w-full h-auto rounded-2xl object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-xl text-gray-600">Get answers to common questions about Ledger devices</p>
          </div>

          <div className="space-y-6">
            {[
              {
                question: "What is a hardware wallet?",
                answer: "A hardware wallet is a physical device that stores your cryptocurrency private keys offline, providing the highest level of security for your digital assets."
              },
              {
                question: "How do I set up my Ledger device?",
                answer: "Simply follow our 6-step setup guide: unbox your device, download Ledger Live, set up your device, create a PIN, write down your recovery phrase, and install apps."
              },
              {
                question: "What happens if I lose my Ledger device?",
                answer: "If you lose your device, you can restore your wallet using your 24-word recovery phrase on a new Ledger device or compatible wallet software."
              },
              {
                question: "Can I use Ledger with mobile devices?",
                answer: "Yes, Ledger Nano X supports Bluetooth connectivity for mobile use. You can manage your crypto on the go with the Ledger Live mobile app."
              }
            ].map((faq, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{faq.question}</h3>
                <p className="text-gray-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Secure Your Crypto?</h2>
          <p className="text-xl mb-8">
            Join millions of users who trust Ledger to keep their digital assets safe. 
            Get started today with our easy setup guide.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-blue-600 px-8 py-4 rounded-full text-lg font-semibold hover:bg-gray-100 whitespace-nowrap cursor-pointer">
              Shop Ledger Devices
            </button>
            <button className="border-2 border-white text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-white hover:text-blue-600 whitespace-nowrap cursor-pointer">
              Download Ledger Live
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-2xl font-bold mb-4" style={{fontFamily: '"Pacifico", serif'}}>
                Ledger
              </h3>
              <p className="text-gray-400 mb-4">
                The most trusted hardware wallet for securing your cryptocurrency investments.
              </p>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Products</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white cursor-pointer">Ledger Nano S Plus</a></li>
                <li><a href="#" className="hover:text-white cursor-pointer">Ledger Nano X</a></li>
                <li><a href="#" className="hover:text-white cursor-pointer">Ledger Stax</a></li>
                <li><a href="#" className="hover:text-white cursor-pointer">Accessories</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white cursor-pointer">Setup Guide</a></li>
                <li><a href="#" className="hover:text-white cursor-pointer">Help Center</a></li>
                <li><a href="#" className="hover:text-white cursor-pointer">Contact Support</a></li>
                <li><a href="#" className="hover:text-white cursor-pointer">Community</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white cursor-pointer">About Us</a></li>
                <li><a href="#" className="hover:text-white cursor-pointer">Careers</a></li>
                <li><a href="#" className="hover:text-white cursor-pointer">Press</a></li>
                <li><a href="#" className="hover:text-white cursor-pointer">Security</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400">© 2024 Ledger. All rights reserved.</p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <a href="#" className="text-gray-400 hover:text-white cursor-pointer">Privacy Policy</a>
              <a href="#" className="text-gray-400 hover:text-white cursor-pointer">Terms of Service</a>
              <a href="https://readdy.ai/?origin=logo" className="text-gray-400 hover:text-white cursor-pointer">Website Builder</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
